import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Calendar, CircleChevronLeft, CircleChevronRight, Expand, Shrink } from "lucide-react";
import SelectField from "../../components/ui/SelectField";
import AIContentAction from "../../actions/AIContentAction";
import { TIMEZONE_OPTIONS } from "../constants/timezoneConstants";
import { buildStatusOptions, getStatusLabel } from "../constants/statusConstants";
import { format } from "date-fns";
import { vi as viLocale, enUS as enUSLocale } from "date-fns/locale";

interface CalendarToolbarProps {
  calendarRef: React.RefObject<any>;
  eventsCount: number;
  onClearPosts: () => void;
  onToggleFullView: () => void;
  isFullCalendarView: boolean;
  currentDate: Date;
  animateMonthLabel?: boolean;
  viewMode?: "calendar" | "media";
  onChangeViewMode?: (mode: "calendar" | "media") => void;
}

const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  calendarRef,
  onToggleFullView,
  isFullCalendarView,
  currentDate,
  animateMonthLabel = false,
  viewMode = "calendar",
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const platformFilter = useSelector(
    (state: any) => state.aiContent?.get?.("platformFilter") || "All"
  );
  const statusFilter = useSelector((state: any) => state.aiContent?.get?.("statusFilter") || "All");
  const timezoneFilter = useSelector(
    (state: any) => state.aiContent?.get?.("timezoneFilter") || "All"
  );
  const statusOptions = buildStatusOptions(t);

  const formatDate = (date: Date) => {
    const currentLang = i18n.language || "en";
    const locale = currentLang.startsWith("vi") ? viLocale : enUSLocale;
    return format(date, "LLLL yyyy", { locale });
  };

  return (
    <div className="ml-5 mb-5 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {/* Today Button */}
        <button
          className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white hover:bg-gray-50 transition-colors"
          onClick={() => {
            calendarRef.current?.getApi()?.today();
            const todayCell = document.querySelector(".fc-day-today");
            todayCell?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        >
          <Calendar className="w-3.5 h-3.5 text-gray-700" strokeWidth={2} />
          <span className="text-sm font-semibold text-gray-700">{t("Today")}</span>
        </button>

        {/* Navigation Arrows */}
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center justify-center w-9.5 h-9.5 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            aria-label={t("Previous")}
            onClick={() => {
              calendarRef.current?.getApi()?.prev();
            }}
          >
            <CircleChevronLeft className="w-4 h-4 text-gray-700" strokeWidth={2} />
          </button>
          <span
            className={`text-base font-bold text-gray-900 ${
              animateMonthLabel ? "month-change-animate" : ""
            }`}
          >
            {formatDate(currentDate)}
          </span>
          <button
            className="inline-flex items-center justify-center w-9.5 h-9.5 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            aria-label={t("Next")}
            onClick={() => {
              calendarRef.current?.getApi()?.next();
            }}
          >
            <CircleChevronRight className="w-4 h-4 text-gray-700" strokeWidth={2} />
          </button>
        </div>

        {/* Filters */}
        <div className="hidden md:flex items-center gap-2 mr-2">
          <div className="w-38">
            <SelectField
              value={{
                value: platformFilter,
                label: (platformFilter === "All" ? t("All Platforms") : platformFilter) as any,
              }}
              onChange={(opt: any) =>
                dispatch(AIContentAction.setPlatformFilter(String(opt?.value || "All")) as any)
              }
              options={[
                { value: "All", label: t("All Platforms") },
                { value: "Instagram", label: t("Instagram") },
                { value: "Facebook", label: t("Facebook") },
                { value: "Twitter", label: t("X (Twitter)") },
                { value: "Threads", label: t("Threads") },
              ]}
              placeholder={t("All Platforms")}
            />
          </div>
          <div className="w-38">
            <SelectField
              value={{
                value: statusFilter,
                label: getStatusLabel(statusFilter, t) as any,
              }}
              onChange={(opt: any) =>
                dispatch(AIContentAction.setStatusFilter(String(opt?.value || "All")))
              }
              options={statusOptions}
              placeholder={t("All Status")}
            />
          </div>
          <div className="w-38">
            <SelectField
              value={{
                value: timezoneFilter,
                label: (timezoneFilter === "All"
                  ? t("All Timezones")
                  : TIMEZONE_OPTIONS.find((o) => o.value === timezoneFilter)?.label ||
                    timezoneFilter) as any,
              }}
              onChange={(opt: any) =>
                dispatch(AIContentAction.setTimezoneFilter(String(opt?.value || "All")) as any)
              }
              options={[{ value: "All", label: t("All Timezones") }, ...TIMEZONE_OPTIONS]}
              placeholder={t("Select Timezone")}
            />
          </div>
        </div>
      </div>

      <div className="items-center gap-2 w-full md:w-auto hidden lg:flex md:ml-auto justify-between md:justify-end flex-wrap">
        {/* Full View Toggle (only in Calendar mode) */}
        {viewMode === "calendar" && (
          <button
            className="inline-flex items-center justify-center w-9.5 h-9.5 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            onClick={onToggleFullView}
            aria-label={t("Toggle full view")}
          >
            {isFullCalendarView ? (
              <Shrink className="w-4 h-4 text-gray-700" strokeWidth={2} />
            ) : (
              <Expand className="w-4 h-4 text-gray-700" strokeWidth={2} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CalendarToolbar;
